import { Component, OnInit, ChangeDetectionStrategy, ViewEncapsulation, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Store, Select } from '@ngxs/store';
import { ViewMode } from '../../models/view-mode.enum';
import { ViewActions } from '../../state/view.actions';
import { ViewState } from '../../state/view.state';
import { Observable, Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged, take } from 'rxjs/operators';
import { Fullscreen } from 'src/app/shared/libraries/fullscreen/fullscreen';
import { ViewService } from '../../view.service';
import { StoryLabelsState } from 'src/app/story/state/labels/story.labels.state';
import { LabelModel } from 'src/app/story/models/labels/label.model';
import { EngineState } from 'src/app/engine/state/engine.state';
import { ViewLayoutStatus } from '../../models/view-layout-status.enum';
import { LayoutService } from '../../layout.service';
import { ViewLayout } from '../../models/view-layout.enum';
import { HistoryService } from 'src/app/history/history.service';

enum MenuModeStatus {
  Opened,
  Closed,
  Reset
}

@Component({
  selector: 'app-view-menu',
  templateUrl: './view-menu.component.html',
  styleUrls: ['./view-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class ViewMenuComponent implements OnInit, OnDestroy {
  // ---------- Observables ----------
  @Select(StoryLabelsState.getChapterLabels)
  labels$: Observable<LabelModel[]>;

  @Select(EngineState.getShowLabels)
  showLabels$: Observable<boolean>;



  // ---------- Class Variables ----------
  open = false;
  canReset = false;
  menuMode: MenuModeStatus = MenuModeStatus.Closed;
  menuModeStatus = MenuModeStatus;
  viewMode = ViewMode;
  viewLayout = ViewLayout;
  canFullscreen = true;
  ngUnsubscribe = new Subject();



  // ---------- Lifecycle Hooks ----------
  constructor(
    private store: Store,
    private viewService: ViewService,
    private layoutService: LayoutService,
    private historyService: HistoryService,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.canFullscreen = Fullscreen.canFullscreen();
    this.initLayoutListener();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initLayoutListener() {
    this.store.select(ViewState.getLayout)
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(layout => {
        const drawerMode = this.viewService.inferDrawerMode(layout);
        const menuMode = (drawerMode !== ViewMode.Content || layout.search.status === ViewLayoutStatus.Show) ? 
          MenuModeStatus.Reset : MenuModeStatus.Closed;
        
        this.toggleMenu(menuMode);
      });
  }

  handleMenuClick(menuMode?: MenuModeStatus) {
    this.toggleMenu(menuMode);
  }

  handleSearchClick() {
    this.toggleMenu(MenuModeStatus.Reset);
    this.viewService.toggleSearch();
  }

  handleToolsClick() {
    this.toggleMenu(MenuModeStatus.Reset);
    const layout = this.store.selectSnapshot(ViewState.getLayout);
    layout.tools.status = ViewLayoutStatus.Show;
    this.store.dispatch(new ViewActions.SetLayout(layout));
  }

  handleFullscreenClick() {
    this.closeMenu();
    Fullscreen.toggleFullscreen();
  }

  handlePresentationClick() {
    this.closeMenu();
    this.viewService.togglePresentation();
  }

  handleHelpClick() {
    this.closeMenu();
    this.viewService.toggleHelp();
  }

  handleNewTabClick() {
    this.viewService.openInNewTab();
  }

  handleToggleLabelsClick() {
    this.viewService.toggleLabels();
  }

  handleResetMenuClick() {
    this.closeMenu();
    this.viewService.resetLayout();
  }

  handleResetSceneClick() {
    this.historyService.resetChapter();
  }

  closeMenu() {
    this.toggleMenu(MenuModeStatus.Closed);
  }
  
  toggleMenu(menuMode?: MenuModeStatus) {
    if (menuMode == null) {
      menuMode = this.menuMode === MenuModeStatus.Closed ? MenuModeStatus.Opened : MenuModeStatus.Closed;
    }

    this.menuMode = menuMode;
    this.changeDetectorRef.markForCheck();
  }

  isEnabled(layout: ViewLayout) {
    return this.layoutService.isLayoutEnabled(layout);
  }
}
